<template>
  <div>
    <section-title
      has-line
      content="Administrador de Propiedades"
      containerClass="tag-badge-info-custom"
    />
    <span style="color:#727272">
      <i class="far fa-info-circle"></i>
      Las Propiedades añadidas estarán disponibles para seleccionar al momento
      de crear un equipo
    </span>

    <div class="row mt-4 px-lg-0 px-md-3">
      <div class="col-lg-3 col-md-6 col-sm-12">
        <validation-observer ref="newPropertyObserver">
          <div class="form-group">
            <validation-provider rules="required" v-slot="{ errors }">
              <custom-input
                label="Añadir Nueva Propiedad:"
                v-model="newProperty"
                custom-error-msg="Este campo es requerido"
                :error="errors[0]"
                @keypress.enter.native="addProperty"
              />
            </validation-provider>
          </div>
        </validation-observer>
      </div>
      <div class="col-lg-3 col-md-6 col-sm-12 pt-2">
        <action-button
          :variant="'success mt-md-4 ' + (isMobile ? 'w-100' : '')"
          type="button"
          i="fas fa-file-plus"
          @click="addProperty"
        >
          A&ntilde;adir Propiedad
        </action-button>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <div
          class="mt-4 "
          v-for="(item, index) in properties"
          :key="index"
          :has-toggle="false"
        >
          <custom-collapse
            v-if="item.visible"
            class="mt-2 mx-1 custom-collapse-properties"
            :class="{ 'px-3': !isMobile, invalid: !item.valid }"
            :id="`tab-${index}`"
          >
            <custom-collapse-item
              expanded-icon="fas fa-plus-circle color-black--3"
              class="rounded"
              :class="{ 'border border-danger': !item.valid }"
              :button-icon="
                isBlocked(item) ? '' : 'far fa-times-circle color-red'
              "
              :parent-id="`tab-${index}`"
              :title="index === 0 ? 'Marca/Modelo' : item.name"
              @actionButtonClick="removeProperty(index)"
            >
              <div class="px-3">
                <property-brand
                  v-if="index === 0"
                  :property="item"
                  :model-id="properties[1].id"
                />
                <property-select
                  v-else-if="index === 2"
                  :property="item"
                  :options="colors"
                />
                <property-input v-else :property="item" />
              </div>
            </custom-collapse-item>
          </custom-collapse>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SectionTitle from 'SectionTitle';
import CustomInput from 'CustomInput';
import ActionButton from 'ActionButton';
import CustomCollapse from '@/components/common/CustomCollapse';
import CustomCollapseItem from '@/components/common/CustomCollapseItem';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import mixAlert from '@/mixins/alert.mixin';
import { name as appModuleName } from '@/modules/app/store/index';
import { mapGetters } from 'vuex';
import PropertyBrand from './components/PropertyBrand.vue';
import PropertySelect from './components/PropertySelect.vue';
import PropertyInput from './components/PropertyInput.vue';

import service from '@/services/api/inventorycatalog.api';

const PROPERTY_MAX_COUNT = 5;

export default {
  name: 'Property',
  components: {
    SectionTitle,
    CustomInput,
    ActionButton,
    ValidationObserver,
    ValidationProvider,
    CustomCollapse,
    CustomCollapseItem,
    PropertyBrand,
    PropertySelect,
    PropertyInput,
  },
  mixins: [mixAlert],
  props: {
    inventoryCatalogId: {
      type: Number,
      required: true,
    },
  },
  data: () => ({
    newProperty: null,
    new: null,
    properties: [],
    colors: [],
  }),
  async mounted() {
    await Promise.allSettled([this.getProperties(), this.getColors()]);
  },
  computed: {
    ...mapGetters(appModuleName, {
      isMobile: 'getIsMobileDensity',
    }),
    isBlocked() {
      return (item) => item.default || item.sealed;
    },
  },
  methods: {
    async getColors() {
      try {
        const { data } = await service.getColors();
        this.colors = data;
      } catch (error) {
        this.ShowToast('Alerta', error?.response?.data?.message, 'error');
      }
    },
    async getProperties() {
      try {
        const { data } = await service.getProperties(this.inventoryCatalogId);
        this.properties = data.map((x, i) => {
          x.visible = i !== 1;
          x.default = i < 4;
          x.valid = true;
          return x;
        });
      } catch (error) {
        this.ShowToast('Alerta', error?.response?.data?.message, 'error');
      }
    },
    async addProperty() {
      const observer = this.$refs['newPropertyObserver'];
      const valid = await observer.validate();
      if (!valid) return;
      const propertyAddedCount = this.properties.filter((x) => x.visible)
        .length;
      if (propertyAddedCount === PROPERTY_MAX_COUNT) {
        this.ShowToast(
          'Alerta',
          `Alcanzó el límite de propiedades (${PROPERTY_MAX_COUNT}) que se pueden añadir a un equipo.`,
          'error'
        );
        return;
      }

      this.properties.push({
        name: this.newProperty,
        visible: true,
        default: false,
        details: [],
        valid: true,
      });
      this.newProperty = null;
      await observer.reset();
    },
    async removeProperty(index) {
      const confirmed = await this.ShowCancelModal(
        'Confirmación',
        `¿Está seguro que desea remover la propiedad <b>${this.properties[index].name}</b>?`,
        'Sí',
        'No'
      );
      if (!confirmed) return;
      this.properties.splice(index, 1);
    },
    async save() {
      try {
        const properties = this.properties.filter((x) => x.visible);
        properties.forEach((x) => (x.valid = x.details.length > 0));
        const valid = properties.every((x) => x.valid);
        if (!valid) {
          this.ShowToast(
            'Alerta',
            'Debe tener al menos un valor en cada propiedad agregada.',
            'error'
          );
        }
        const brandsValid = properties[0].details.every(
          (x) => x.children.length > 0
        );
        if (!brandsValid) {
          properties[0].valid = false;
          this.ShowToast(
            'Alerta',
            'Cada marca debe tener al menos un modelo agregado.',
            'error'
          );
          return;
        }
        if (!valid) return;
        await service.addProperties(this.inventoryCatalogId, properties);
        this.ShowToast(
          'Éxito',
          'Información ha sido guardada exitosamente.',
          'success'
        );
      } catch (error) {
        this.ShowToast('Alerta', error?.response?.data.message, 'error');
      }
    },
  },
};
</script>

<style scoped>
.status-indicator {
  width: 12px;
  height: 12px;
  float: left;
  border-radius: 50%;
  margin-right: 5px;
}

.custom-collapse-properties.invalid ::v-deep .title-pei {
  color: #c9545e !important;
}
</style>
